import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { throwError as observableThrowError, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Platform } from '@ionic/angular';
import { CacheService } from 'ionic-cache';
import { NavController } from '@ionic/angular';

import _ from 'lodash';
import { User } from './login.interface';

import { ServiceProvider } from '../../service/provider.interface';
import { CommonService } from '../../service/common.service';

@Injectable()
export class LoginProvider extends ServiceProvider {
  private endPoint: string;
  private materialEndPoint: string;
  private machineEndpoint: string;
  private pdfSupportInfoEndpoint: string;
  private endmillSearchStringsEndpoint: string;
  private latestToolVersionEndpoint: string;
  constructor(
    public cache: CacheService,
    private http: HttpClient,
    public navCtrl: NavController,
    private commonService: CommonService,
    private platform: Platform
  ) {
    super();
    this.endPoint = this.config.apiEndpoints['login'];
    this.materialEndPoint = this.config.apiEndpoints['materials'];
    this.machineEndpoint = this.config.apiEndpoints['machine'];
    this.pdfSupportInfoEndpoint = this.config.apiEndpoints['pdfSupportInfo'];
    this.endmillSearchStringsEndpoint = this.config.apiEndpoints['endmillSearchStrings'];
    this.latestToolVersionEndpoint = this.config.apiEndpoints['latestToolVersion'];
  }

  public login = (user: User): Observable<any> => {
    const url: string = this.endPoint;

    const params: HttpParams = new HttpParams({
      fromObject: {
        userName: user.userName.trim(),
        password: user.password,
        grant_type: 'password'
      }
    });

    const login = this.http
      .post(url, params)
      .pipe(
        catchError((response: HttpErrorResponse) =>
          observableThrowError(response.error.error_description || 'Server error')
        )
      );

    return this.cache.loadFromObservable(url, login);
  };

  public getMaterials = (loginResponse: { access_token: string }): Observable<any> => {
    const url: string = this.materialEndPoint;

    const headers: HttpHeaders = this.createAuthorizationHeader(loginResponse.access_token);

    return this.http
      .get(url, {
        headers: headers
      })
      .pipe(
        map(items => {
          let materials = _.uniq(
            _.map(items, item => {
              return item.name + ' — ' + item.description;
            })
          );

          let subgroups = _.uniq(
            _.map(items, item => {
              return item.subGroup;
            })
          );

          let conditions = _.uniq(
            _.map(items, item => {
              return item.condition;
            })
          );

          let extendedItems = _.map(items, item => {
            return _.extend(item, {
              uiName: item.name + ' — ' + item.description
            });
          });

          let response = {
            materials: materials,
            subgroups: subgroups,
            conditions: conditions,
            all: extendedItems
          };

          return response;
        })
      );
  };

  public getMachine = (loginResponse: { access_token: string }): Observable<any> => {
    const url: string = this.machineEndpoint;
    const headers: HttpHeaders = this.createAuthorizationHeader(loginResponse.access_token);

    return this.http
      .get(url, {
        headers: headers
      })
      .pipe(
        map(response => {
          return response;
        }),
        catchError((response: HttpErrorResponse) =>
          observableThrowError(response.error.error_description || 'Server error')
        )
      );
  };

  public getPdfSupportInfo = (loginResponse: { access_token: string }): Observable<any> => {
    const url: string = this.pdfSupportInfoEndpoint;
    const headers: HttpHeaders = this.createAuthorizationHeader(loginResponse.access_token);

    return this.http
      .get(url, {
        headers: headers
      })
      .pipe(
        map(response => {
          return response;
        }),
        catchError((response: HttpErrorResponse) =>
          observableThrowError(response.error.error_description || 'Server error')
        )
      );
  };

  public getEndmillSearchStrings = (loginResponse: { access_token: string }): Observable<any> => {
    const url: string = this.endmillSearchStringsEndpoint;
    const headers: HttpHeaders = this.createAuthorizationHeader(loginResponse.access_token);

    return this.http
      .get(url, {
        headers: headers
      })
      .pipe(
        map(response => {
          return response;
        }),
        catchError((response: HttpErrorResponse) =>
          observableThrowError(response.error.error_description || 'Server error')
        )
      );
  };

  public getLatestToolVersion = (loginResponse: { access_token: string }): Observable<number> => {
    const url: string = this.latestToolVersionEndpoint;
    const headers: HttpHeaders = this.createAuthorizationHeader(loginResponse.access_token);

    return this.http
      .get(url, {
        headers: headers
      })
      .pipe(
        map((response: number) => {
          return response;
        }),
        catchError((response: HttpErrorResponse) =>
          observableThrowError(response.error.error_description || 'Server error')
        )
      )
  }

  public logOut = async () => {
    // Log data for user if user logs out
    this.commonService.notifyOther({
      option: 'logdata',
      value: {
        logLocation: 'session end'
      }
    });

    let isEdge = navigator.userAgent.indexOf('Edge') > -1;
    if ('serviceWorker' in navigator && !this.platform.is('ios') && !isEdge) {
      // cannot be done cause as of iOS 11, there is no service worker yet
      navigator.serviceWorker.getRegistrations().then(
        registrations => {
          for (let registration of registrations) {
            registration.unregister();
          }
        },
        err => {
          console.error('Error with service worker:', err);
        }
      );
    }
    // add clean up, atleast what browser can do
    // the even removing an deleting all data, the browser still holds onto the memory till the browser is
    // either hard refresh or closed
    if (typeof caches !== 'undefined') {
      await caches.keys().then(function(keys) {
        keys.forEach(function(request, index, array) {
          caches.delete(request);
        });
      });
    }

    this.cache.removeItem('loginInfo');

    this.commonService.notifyOther({
      option: 'logout',
      value: {},
      emit: false
    });

    indexedDB.deleteDatabase('__ionicCache');
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  };
}
